import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Badge, Button, Form, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "../ActivationRequests/ActivationRequests.css";
import "./PayoutLedger.css";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "");
const fmtDay = (d) => (d ? moment(d).format("DD MMM YYYY") : "");
const EXPORT_COLUMNS = [
  { header: "Payout #", value: (r) => r.payout_Id },
  { header: "Date", value: (r) => fmt(r.date) },
  { header: "Username", value: (r) => r.username },
  { header: "Type", value: (r) => (r.mode === "id_settlement" ? `ID Settlement #${r.settlement_Id}` : "Payout") },
  { header: "Income period", value: (r) => (r.mode === "id_settlement" ? "" : `${r.period?.from ? fmtDay(r.period.from) : "Start"} - ${fmtDay(r.period?.to || r.date)}`) },
  { header: "Breakdown", value: (r) => (r.breakdown || []).map((b) => `${b.label}: ${inr(b.amount)}`).join("; ") },
  { header: "Income", value: (r) => inr(r.gross) },
  { header: "Deductions", value: (r) => inr(r.charges) },
  { header: "To bank", value: (r) => inr(r.net) },
  { header: "Bank", value: (r) => (r.account?.last4 ? `${r.account.bankName || ""} ****${r.account.last4}` : "") },
  { header: "Status", value: (r) => r.status },
  { header: "UTR", value: (r) => r.utr },
];
const STATUS = {
  pending: { label: "Pending", bg: "warning", text: "dark" },
  paid: { label: "Paid", bg: "success" },
  rejected: { label: "Rejected", bg: "danger" },
};
const EMPTY = { search: "", status: "", from: "", to: "" };

/** Every payout with the incomes it is made of (written by the auto payout). */
const PayoutLedger = () => {
  const [filters, setFilters] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
    const { data, error } = await apiCall("GET", `/payout-ledger?${params.toString()}`);
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load the payout ledger.");
    setRows(data?.data || []);
    setSummary(data?.summary || null);
    setPages(data?.pages || 1);
    setTotal(data?.total || 0);
  }, [applied, page]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setApplied(filters);
    }, 350);
    return () => clearTimeout(t);
  }, [filters]);

  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const active = Object.values(applied).filter(Boolean).length;

  return (
    <div className="plan-settings activation-requests-page payout-ledger-page">
      <div className="ps-header">
        <div>
          <h2>Payout Ledger</h2>
          <p>Each automatic payout with the incomes it includes, the period they were earned in and the bank account it goes to.</p>
        </div>
        <ExportButtons
          title="Payout Ledger"
          columns={EXPORT_COLUMNS}
          filters={Object.entries(applied).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(", ")}
          getRows={() =>
            collectPages(async (p, l) => {
              const params = new URLSearchParams({ page: p, limit: l });
              Object.entries(applied).forEach(([k, v]) => v && params.set(k, v));
              const { data } = await apiCall("GET", `/payout-ledger?${params.toString()}`);
              return { rows: data?.data || [], pages: data?.pages || 1 };
            })
          }
        />
      </div>

      <div className="ps-stats ar-stats">
        <div className="ps-stat ar-stat ar-stat--pending">
          <span>Pending</span>
          <strong>{summary?.pending ?? 0}</strong>
          <small>waiting to be paid</small>
        </div>
        <div className="ps-stat ar-stat ar-stat--approved">
          <span>Paid</span>
          <strong>{summary?.paid ?? 0}</strong>
          <small>payouts</small>
        </div>
        <div className="ps-stat ar-stat">
          <span>Total {active ? "(filtered)" : ""}</span>
          <strong>{inr(summary?.net)}</strong>
          <small>to bank, from {inr(summary?.gross)} income</small>
        </div>
      </div>

      <div className="ps-panel" style={{ borderTop: "1px solid var(--ps-border)", borderRadius: "var(--ps-radius)" }}>
        <div className="ar-panel-title">
          <h3>Payouts</h3>
          <span className="ar-count">{total}</span>
          {active > 0 && <span className="ar-filter-badge">{active} filter{active > 1 ? "s" : ""}</span>}
        </div>

        <div className="ar-filters">
          <div className="ar-f-search">
            <Form.Label>Search</Form.Label>
            <Form.Control size="sm" placeholder="Username or payout #" value={filters.search} onChange={set("search")} />
          </div>
          <div>
            <Form.Label>Status</Form.Label>
            <Form.Select size="sm" value={filters.status} onChange={set("status")}>
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="rejected">Rejected</option>
            </Form.Select>
          </div>
          <div>
            <Form.Label>From</Form.Label>
            <Form.Control size="sm" type="date" value={filters.from} onChange={set("from")} />
          </div>
          <div>
            <Form.Label>To</Form.Label>
            <Form.Control size="sm" type="date" value={filters.to} onChange={set("to")} />
          </div>
          <div className="ar-f-reset">
            <Button size="sm" variant="outline-secondary" disabled={!active} onClick={() => setFilters(EMPTY)}>
              Reset
            </Button>
          </div>
        </div>

        <div className="ps-table-wrap">
          <Table className="ps-table">
            <thead>
              <tr>
                <th>Payout</th>
                <th>Member</th>
                <th>Income period</th>
                <th>Income</th>
                <th>Deductions</th>
                <th>To bank</th>
                <th>Status</th>
                <th className="text-end">Incomes</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={8} className="ps-empty">{active ? "No payouts match these filters." : "No payouts yet. They appear here when the automatic payout runs."}</td></tr>
              )}
              {!loading &&
                rows.map((r) => {
                  const isOpen = open === r.payout_Id;
                  const st = STATUS[r.status] || STATUS.pending;
                  const isSettle = r.mode === "id_settlement";
                  return (
                    <Fragment key={r.payout_Id}>
                      <tr>
                        <td>
                          <div className="ps-strong">#{r.payout_Id}</div>
                          {isSettle && <div className="ps-small ps-type-advisor">ID Settlement #{r.settlement_Id}</div>}
                          <div className="ps-muted ps-small">{moment(r.date).format("DD MMM YYYY, HH:mm")}</div>
                        </td>
                        <td>
                          <div className="ps-strong">{r.username}</div>
                          {r.account?.last4 && (
                            <div className="ps-muted ps-small">{r.account.bankName} ····{r.account.last4}</div>
                          )}
                        </td>
                        <td className="ps-small">
                          {isSettle
                            ? `Investment paid back${r.utr ? ` · UTR ${r.utr}` : ""}`
                            : `${r.period?.from ? moment(r.period.from).format("DD MMM YYYY") : "Start"} – ${moment(r.period?.to || r.date).format("DD MMM YYYY")}`}
                        </td>
                        <td>{inr(r.gross)}</td>
                        <td>{r.charges > 0 ? `− ${inr(r.charges)}` : "—"}</td>
                        <td className="ps-strong">{inr(r.net)}</td>
                        <td><Badge bg={st.bg} text={st.text}>{st.label}</Badge></td>
                        <td className="text-end">
                          <Button size="sm" variant={isOpen ? "primary" : "outline-primary"} onClick={() => setOpen(isOpen ? null : r.payout_Id)}>
                            {isOpen ? "Hide" : isSettle ? "Details" : `${r.breakdown.length} income${r.breakdown.length === 1 ? "" : "s"}`}
                          </Button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="pl-breakdown-row">
                          <td colSpan={8}>
                            <div className="pl-breakdown">
                              {r.breakdown.map((b) => (
                                <div className="pl-line" key={b.source}>
                                  <span>{b.label}</span>
                                  <span className="ps-muted">{b.count ? `${b.count} entr${b.count === 1 ? "y" : "ies"}` : ""}</span>
                                  <strong>{b.amount < 0 ? `− ${inr(-b.amount)}` : inr(b.amount)}</strong>
                                </div>
                              ))}
                              <div className="pl-line pl-line--total">
                                <span>{isSettle ? "Settlement amount" : "Total income"}</span>
                                <span />
                                <strong>{inr(r.gross)}</strong>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
            </tbody>
          </Table>
        </div>

        <div className="ar-pagination">
          <div className="ar-pagination-info">
            Showing <strong>{total ? (page - 1) * limit + 1 : 0}–{Math.min(page * limit, total)}</strong> of <strong>{total}</strong>
          </div>
          <div className="ar-pages">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
            <span className="ps-muted ps-small">Page {page} of {pages}</span>
            <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayoutLedger;
